import './styles.scoped.css'
import {useState, useEffect, useRef} from 'react'
import {Avatar, Typography, InputAdornment, Modal, TextField, IconButton} from '@mui/material'
import {Close, Search, DateRange, AccessTime} from '@mui/icons-material'
import {evvApi} from '../../../../services'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {useMobile} from '../../../../hooks'
import {HhaxCardsSkeleton} from '../DashboardSkeletons'
import {parseError, getColorFromName, getInitials, ignoreTimezone, formatTime} from '../../../../utils/helpers'
import {format} from 'date-fns'
import {useNavigate} from 'react-router-dom'

const isoTime = iso => (iso ? format(new Date(iso), 'HH:mm') : null)

export default ({open, setOpen}) => {
    const {mobile} = useMobile()
    const navigate = useNavigate()
    const [items, setItems] = useState([])
    const [searchTyping, setSearchTyping] = useState('')
    const [search, setSearch] = useState('')
    const [skeleton, setSkeleton] = useState(true)
    const requestId = useRef(0)
    const {setMessageBox, branchId} = useGlobalStates()

    useEffect(() => {
        const timeout = setTimeout(() => setSearch(searchTyping), 500)
        return () => clearTimeout(timeout)
    }, [searchTyping])

    useEffect(() => {
        if (open) {
            loadData()
        }
    }, [open, branchId, search])

    const loadData = () => {
        if (!branchId) {
            return
        }
        const id = ++requestId.current
        setItems([])
        setSkeleton(true)
        evvApi
            .getVisits({branch_id: branchId, status: 'rejected', search: search || null, page: 1, page_size: 100})
            .then(({data: {visits}}) => {
                if (id !== requestId.current) return
                setItems(visits || [])
                setSkeleton(false)
            })
            .catch(error => {
                if (id !== requestId.current) return
                setSkeleton(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    const action = item => {
        navigate(`/evv/${item?.visitId}`)
    }

    const reason = item => item?.hhax?.lastError || (item?.warnings?.length ? item.warnings.join(', ') : 'No additional details provided.')

    return (
        <Modal open={open}>
            <div className="container">
                <div className="content">
                    <div className="header">
                        <Typography variant="h6" color="text.primary">
                            Rejected HHAX Sync
                        </Typography>
                        <div className="form">
                            <div className="view-more" onClick={() => navigate('/evv')}>
                                <Typography variant="h8" color="primary">
                                    View More
                                </Typography>
                            </div>
                            <IconButton onClick={() => setOpen(false)}>
                                <Close />
                            </IconButton>
                        </div>
                    </div>
                    <br />
                    {mobile && (
                        <>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    )}
                    <TextField
                        placeholder="Search client or caregiver or service ..."
                        fullWidth
                        onChange={e => setSearchTyping(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    <br />
                    {skeleton && <HhaxCardsSkeleton reason />}
                    {items.map((item, index) => (
                        <div key={item?.visitId || index}>
                            <br />
                            <div className="card" onClick={() => action(item)}>
                                <Typography variant="h8" color="text.primary">
                                    {item?.service}
                                </Typography>
                                <div className="people-row">
                                    <div>
                                        <Typography variant="caption" color="primary">
                                            CLIENT
                                        </Typography>
                                        <div className="people-card">
                                            {item?.client?.avatar_url ? (
                                                <Avatar src={item.client.avatar_url} style={{width: 40, height: 40}} />
                                            ) : (
                                                <Avatar
                                                    style={{
                                                        backgroundColor: getColorFromName(item?.client?.name),
                                                        width: 40,
                                                        height: 40,
                                                        fontSize: 20,
                                                    }}>
                                                    {getInitials(item?.client?.name)}
                                                </Avatar>
                                            )}
                                            <div className="people-profile-card">
                                                <Typography variant="h8" color="text.primary">
                                                    {item?.client?.name}
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <Typography variant="caption" color="primary">
                                            CAREGIVER
                                        </Typography>
                                        <div className="people-card">
                                            {item?.caregiver?.avatar_url ? (
                                                <Avatar src={item.caregiver.avatar_url} style={{width: 40, height: 40}} />
                                            ) : (
                                                <Avatar
                                                    style={{
                                                        backgroundColor: getColorFromName(item?.caregiver?.name),
                                                        width: 40,
                                                        height: 40,
                                                        fontSize: 20,
                                                    }}>
                                                    {getInitials(item?.caregiver?.name)}
                                                </Avatar>
                                            )}
                                            <div className="people-profile-card">
                                                <Typography variant="h8" color="text.primary">
                                                    {item?.caregiver?.name}
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="datetime-row">
                                    <div className="datetime-col">
                                        <Typography variant="overline" color="text.primary">
                                            Scheduled:
                                        </Typography>
                                        <div className="datetime">
                                            <div className="row-content">
                                                <Typography variant="body3" color="text.secondary">
                                                    <DateRange fontSize="small" />
                                                </Typography>
                                                <Typography variant="body3" color="text.secondary">
                                                    {item?.date ? format(new Date(ignoreTimezone(item?.date)), 'EEE, dd MMM yyyy') : '-'}
                                                </Typography>
                                            </div>
                                            <Typography variant="body3" color="text.secondary">
                                                |
                                            </Typography>
                                            <div className="row-content">
                                                <Typography variant="body3" color="text.secondary">
                                                    <AccessTime fontSize="small" />
                                                </Typography>
                                                <Typography variant="body3" color="text.secondary">
                                                    {formatTime(isoTime(item?.scheduled?.start))} - {formatTime(isoTime(item?.scheduled?.end))}
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="datetime-col">
                                        <Typography variant="overline" color="text.primary">
                                            Report:
                                        </Typography>
                                        <div className="datetime">
                                            {item?.actual?.start ? (
                                                <>
                                                    <div className="row-content">
                                                        <Typography variant="body3" color="text.secondary">
                                                            <DateRange fontSize="small" />
                                                        </Typography>
                                                        <Typography variant="body3" color="text.secondary">
                                                            {format(new Date(item?.actual?.start), 'EEE, dd MMM yyyy')}
                                                        </Typography>
                                                    </div>
                                                    <Typography variant="body3" color="text.secondary">
                                                        |
                                                    </Typography>
                                                    <div className="row-content">
                                                        <Typography variant="body3" color="text.secondary">
                                                            <AccessTime fontSize="small" />
                                                        </Typography>
                                                        <Typography variant="body3" color="text.secondary">
                                                            {formatTime(isoTime(item?.actual?.start))} - {formatTime(isoTime(item?.actual?.end))}
                                                        </Typography>
                                                    </div>
                                                </>
                                            ) : (
                                                <Typography variant="body3" color="text.secondary">
                                                    Not reported yet
                                                </Typography>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <Typography variant="overline" color="error" style={{marginTop: 2}}>
                                    REASON:
                                </Typography>
                                <Typography variant="body3" color="text.secondary" style={{marginTop: -8}}>
                                    {reason(item)}
                                </Typography>
                            </div>
                        </div>
                    ))}
                    <br />
                    <br />
                    {mobile && (
                        <>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    )}
                </div>
            </div>
        </Modal>
    )
}
